import React, { useEffect, useState } from 'react';
import { api } from '../app/api';
import { logger } from '../lib/logger';
import { Mic, Lock, Clock, ExternalLink, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';

/**
 * AudioDropsShelf — Phase 24
 *
 * Public-facing shelf that renders Coach Lewis's audio drops on /locker-room.
 * Free users see locked tiles + an upgrade CTA. Pass holders see an inline
 * <audio> player (for direct MP3 URLs) or an external-open button (for
 * Spotify / SoundCloud share links).
 *
 * Empty state is intentionally tight — "Coach Lewis is recording the first
 * drops" — so the shelf doesn't collapse when the library is empty.
 */

const CATEGORY_LABELS = {
  pregame: 'Game-Day Pump-Up',
  post_loss: 'Post-Loss Reset',
  mid_season: 'Mid-Season Grind',
  mindset: 'Mindset Drop',
};

function formatDuration(sec) {
  if (!sec || isNaN(sec)) return null;
  const n = Math.max(0, Math.round(sec));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function isDirectAudio(url) {
  if (!url) return false;
  return /\.(mp3|m4a|wav|aac|ogg)(\?.*)?$/i.test(url);
}

export const AudioDropsShelf = ({ isPro, onUpgrade }) => {
  const [drops, setDrops] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/api/audio-drops')
      .then((data) => {
        if (!active) return;
        setDrops(Array.isArray(data?.audio_drops) ? data.audio_drops : []);
      })
      .catch((err) => {
        if (!active) return;
        logger.warn('audio drops load failed', err);
        setDrops([]);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <div className="mb-12" data-testid="locker-audio-drops-shelf">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Mic className="h-4 w-4 text-[#4FBCC7]" />
        <h3 className="font-heading text-[1.1rem] font-semibold tracking-tight text-white">
          Coach Lewis Audio Drops
        </h3>
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#4FBCC7]/15 ring-1 ring-[#4FBCC7]/30 font-mono-accent uppercase tracking-[0.16em] text-[9px] text-[#4FBCC7] font-semibold"
          title="Locker Room Pass benefit"
        >
          <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.5} />
          Pass Only
        </span>
      </div>
      <p className="text-[13.5px] text-white/55 mb-5 max-w-[58ch] leading-snug">
        90-second locker-room talks from Coach Lewis &mdash; game-day pump-ups, post-loss resets, mid-season grind fuel. New drops weekly.
      </p>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[112px] rounded-2xl bg-white/[0.04] ring-1 ring-white/8 animate-pulse" />
          ))}
        </div>
      ) : drops.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center" data-testid="locker-audio-drops-empty">
          <Mic className="h-8 w-8 mx-auto text-white/30 mb-3" />
          <p className="text-[14px] font-semibold text-white/85 mb-1">First drops dropping soon.</p>
          <p className="text-[13px] text-white/55 max-w-[40ch] mx-auto">
            Coach Lewis is recording the inaugural drops. Pass holders will be notified the moment they go live.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {drops.map((d) => (
            <AudioDropTile
              key={d.id}
              drop={d}
              isPro={isPro}
              onUpgrade={onUpgrade}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AudioDropTile = ({ drop, isPro, onUpgrade }) => {
  const direct = isDirectAudio(drop.audio_url);
  const duration = formatDuration(drop.duration_sec);
  const categoryLabel = CATEGORY_LABELS[drop.category] || 'Mindset Drop';

  // Locked tile (free user)
  if (!isPro) {
    return (
      <button
        type="button"
        onClick={() => onUpgrade && onUpgrade(`audio-drop:${drop.id}`)}
        data-testid={`locker-audio-tile-locked-${drop.id}`}
        className="group relative text-left rounded-2xl p-4 bg-white/[0.04] ring-1 ring-white/10 hover:ring-[#4FBCC7]/40 hover:bg-white/[0.07] transition-all active:scale-[0.99]"
      >
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0B0C0D] ring-1 ring-[#4FBCC7]/30 flex items-center justify-center shrink-0 group-hover:bg-[#004C54] transition-colors">
            <Lock className="h-4 w-4 text-white/85" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-[#4FBCC7] font-bold">
                {categoryLabel}
              </span>
              {duration && (
                <span className="inline-flex items-center gap-1 text-[10.5px] text-white/45">
                  <Clock className="h-3 w-3" />{duration}
                </span>
              )}
            </div>
            <div className="font-heading text-[14.5px] font-semibold text-white leading-tight truncate">
              {drop.title}
            </div>
            <div className="mt-1.5 font-mono-accent uppercase tracking-[0.14em] text-[10px] text-white/45 group-hover:text-[#4FBCC7] transition-colors">
              Unlock with Pass &rarr;
            </div>
          </div>
        </div>
      </button>
    );
  }

  // Unlocked tile (Pass holder)
  return (
    <div
      className="rounded-2xl p-4 bg-white/[0.05] ring-1 ring-white/10 hover:ring-[#4FBCC7]/40 transition-colors"
      data-testid={`locker-audio-tile-${drop.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="h-11 w-11 rounded-xl bg-[#004C54] flex items-center justify-center shrink-0">
          <Mic className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-[#4FBCC7] font-bold">
              {categoryLabel}
            </span>
            {duration && (
              <span className="inline-flex items-center gap-1 text-[10.5px] text-white/45">
                <Clock className="h-3 w-3" />{duration}
              </span>
            )}
          </div>
          <div className="font-heading text-[14.5px] font-semibold text-white leading-tight">
            {drop.title}
          </div>
          {drop.description && (
            <p className="text-[12px] text-white/55 mt-1 leading-snug line-clamp-2">
              {drop.description}
            </p>
          )}
        </div>
      </div>
      {direct ? (
        <audio
          controls
          preload="metadata"
          src={drop.audio_url}
          className="w-full h-9 rounded-md"
          data-testid={`locker-audio-player-${drop.id}`}
        >
          <track kind="captions" />
        </audio>
      ) : (
        <Button
          asChild
          className="w-full h-9 rounded-md bg-white/8 hover:bg-white/15 text-white border-0"
        >
          <a
            href={drop.audio_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`locker-audio-external-${drop.id}`}
          >
            Open in player <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </a>
        </Button>
      )}
    </div>
  );
};

export default AudioDropsShelf;
